import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { StoryFunnel } from "@quizzes/_story-funnel-schema";
import { api, ApiError } from "../api";
import type { StoryFunnelReport, StoryMetricKey, StoryStep } from "../story-funnel/types";
import { DateRangePicker, isoDaysAgo, isoToday } from "./DateRangePicker";

const METRICS: { key: StoryMetricKey; label: string }[] = [
  { key: "visits", label: "Visits" },
  { key: "completions", label: "Completed stories" },
  { key: "skips", label: "Skipped to offer" },
  { key: "offer_reach", label: "Offer reached" },
  { key: "offer_clicks", label: "Offer clicks" },
  { key: "attributed_purchases", label: "Attributed purchases" },
];

const count = (value: unknown) => typeof value === "number" && Number.isFinite(value) && value >= 0
  ? value.toLocaleString("en-US") : "—";
const percent = (value: unknown) => typeof value === "number" && Number.isFinite(value)
  ? `${Number(value.toFixed(1))}%` : "—";

function exclusiveRange(from: string, to: string) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(from) || !/^\d{4}-\d{2}-\d{2}$/.test(to) || from > to) return null;
  const end = new Date(`${to}T00:00:00.000Z`);
  if (!Number.isFinite(end.getTime())) return null;
  end.setUTCDate(end.getUTCDate() + 1);
  return { from: `${from}T00:00:00.000Z`, to: end.toISOString() };
}

function incomplete(report: StoryFunnelReport, story: StoryFunnel) {
  return METRICS.some(({ key }) => count(report[key]) === "—")
    || !Array.isArray(report.daily)
    || !Array.isArray(report.steps)
    || story.screens.some(screen => !report.steps?.some(step => step.screen_id === screen.id))
    || !report.attribution;
}

export function StoryFunnelAnalytics({ story }: { story: StoryFunnel }) {
  const [range, setRange] = useState(() => ({ from: isoDaysAgo(7), to: isoToday() }));
  const [report, setReport] = useState<StoryFunnelReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [retry, setRetry] = useState(0);
  const validRange = exclusiveRange(range.from, range.to);

  useEffect(() => {
    const dates = exclusiveRange(range.from, range.to);
    const controller = new AbortController();
    let active = true;
    setReport(null);
    setError(null);
    if (!dates) {
      setLoading(false);
      return () => { active = false; controller.abort(); };
    }
    setLoading(true);
    api.storyFunnel({ slug: story.slug, ...dates }, controller.signal)
      .then(result => {
        if (!active) return;
        if (result.story_slug !== story.slug) throw new Error("The API returned data for a different story. Retry to refresh this report.");
        if (result.unit !== "visits") throw new Error("This story report format is not available. Update the API and retry.");
        setReport(result);
      })
      .catch((reason: unknown) => {
        if (!active) return;
        setError(reason instanceof ApiError && reason.status === 404
          ? "Story analytics are not available on this API deployment yet. Retry after the API is updated."
          : reason instanceof Error ? reason.message : "Unable to load story analytics.");
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; controller.abort(); };
  }, [story.slug, range.from, range.to, retry]);

  const steps = new Map<string, StoryStep>((Array.isArray(report?.steps) ? report.steps : []).map(step => [step.screen_id, step]));
  const daily = Array.isArray(report?.daily) ? report.daily : [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link className="text-sm text-mars-orange hover:underline" to={`/quizzes/${story.slug}`}>Story details</Link>
          <h1 className="mt-2 text-2xl font-semibold text-mars-white">{story.meta.title}</h1>
          <p className="mt-1 text-sm text-mars-mute">Story engagement and attributed purchases</p>
        </div>
        <DateRangePicker {...range} onChange={setRange} />
      </div>

      {!validRange && <p role="alert" className="text-sm text-mars-orange">Choose a valid date range with From on or before To.</p>}
      {error && <div role="alert" className="rounded-lg border border-mars-line p-4 text-sm">
        <p>{error}</p>
        <button className="mt-3 text-mars-orange underline" onClick={() => setRetry(value => value + 1)}>Retry</button>
      </div>}
      {report && incomplete(report, story) && <p role="alert" className="text-sm text-mars-mute">Some report fields are unavailable on this API deployment. Missing values appear as —.</p>}
      {loading && <p role="status" className="text-sm text-mars-mute">Loading story analytics…</p>}

      <section aria-label="Story metrics" aria-busy={loading} className="grid grid-cols-2 gap-3 lg:grid-cols-3">
        {METRICS.map(({ key, label }) => <dl key={key} className="rounded-lg border border-mars-line bg-mars-charcoal p-4">
          <dt className="text-sm text-mars-mute">{label}</dt>
          <dd className="mt-2 text-2xl font-semibold text-mars-white">{count(report?.[key])}</dd>
        </dl>)}
      </section>

      <div className="overflow-x-auto rounded-lg border border-mars-line">
        <table aria-label="Story screen funnel" className="w-full text-left text-sm">
          <thead className="text-mars-mute"><tr>
            {["Screen", "Viewed", "Advanced", "Skipped", "Drop-off", "Drop-off rate"].map(label => <th className="px-4 py-3 font-medium" key={label} scope="col">{label}</th>)}
          </tr></thead>
          <tbody>{story.screens.map((screen, index) => {
            const step = steps.get(screen.id);
            return <tr key={screen.id} className="border-t border-mars-line">
              <th scope="row" className="px-4 py-3 font-normal"><span className="text-mars-mute">{index + 1}. </span>{screen.heading}</th>
              {[step?.viewed, step?.advanced, step?.skipped, step?.drop_off].map((value, i) => <td className="px-4 py-3" key={i}>{count(value)}</td>)}
              <td className="px-4 py-3">{percent(step?.drop_off_pct)}</td>
            </tr>;
          })}</tbody>
        </table>
      </div>
      <p className="text-sm text-mars-mute">Visits count separate story attempts. Engagement follows the UTC day each visit began, including screens viewed after midnight. Completion requires progressing through every screen; skipping directly to the offer does not complete a story.</p>
      {report?.attribution && <p className="text-sm text-mars-mute">Purchases count distinct orders credited to the last story offer click within {report.attribution.window_hours} hours. Purchase dates use the order creation time in UTC.</p>}

      {daily.length > 0 && <div className="overflow-x-auto rounded-lg border border-mars-line">
        <table aria-label="Daily story analytics" className="w-full text-left text-sm">
          <thead className="text-mars-mute"><tr><th className="px-4 py-3" scope="col">UTC date</th>{METRICS.map(({ key, label }) => <th className="px-4 py-3" scope="col" key={key}>{label}</th>)}</tr></thead>
          <tbody>{daily.map(day => <tr key={day.date} className="border-t border-mars-line"><th scope="row" className="px-4 py-3 font-normal">{day.date}</th>{METRICS.map(({ key }) => <td className="px-4 py-3" key={key}>{count(day[key])}</td>)}</tr>)}</tbody>
        </table>
      </div>}
    </div>
  );
}
